import React, { useState } from 'react';
import { X, ListChecks, Sparkle, Check } from '@phosphor-icons/react';
import { MERCHANTS } from './constants';

export default function ShoppingListModal({ api, prefs, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const defaultTo = new Date(Date.now() + 6 * 86400000).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(defaultTo);
  const [lang, setLang] = useState((prefs?.kitchen_language === 'hi') ? 'hi' : 'en');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [list, setList] = useState(null);
  const [copiedCat, setCopiedCat] = useState(null);
  const [copiedAll, setCopiedAll] = useState(false);

  const dayCount = (() => {
    try {
      const a = new Date(dateFrom); const b = new Date(dateTo);
      return Math.round((b - a) / 86400000) + 1;
    } catch (_) { return 0; }
  })();

  const setPreset = (k) => {
    const base = new Date();
    if (k === 'today') { setDateFrom(today); setDateTo(today); }
    else if (k === 'thisWeek') {
      const d = new Date(); const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      const mon = new Date(d.setDate(diff));
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      setDateFrom(mon.toISOString().slice(0, 10));
      setDateTo(sun.toISOString().slice(0, 10));
    }
    else if (k === 'nextWeek') {
      const d = new Date(); const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1) + 7;
      const mon = new Date(d.setDate(diff));
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      setDateFrom(mon.toISOString().slice(0, 10));
      setDateTo(sun.toISOString().slice(0, 10));
    }
    else if (typeof k === 'number') {
      const end = new Date(base); end.setDate(end.getDate() + k - 1);
      setDateFrom(base.toISOString().slice(0, 10));
      setDateTo(end.toISOString().slice(0, 10));
    }
  };

  const generate = async () => {
    setErr(''); setList(null);
    if (dayCount < 1 || dayCount > 31) { setErr('Range must be 1–31 days.'); return; }
    setBusy(true);
    try {
      const r = await api.post('/meals/shopping-list', { date_from: dateFrom, date_to: dateTo, lang });
      setList(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to build shopping list.');
    }
    setBusy(false);
  };

  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      if (key === 'all') { setCopiedAll(true); setTimeout(() => setCopiedAll(false), 2000); }
      else { setCopiedCat(key); setTimeout(() => setCopiedCat(null), 2000); }
    } catch (_) {
      window.prompt('Copy this list:', text);
    }
  };

  const allItemsText = list?.categories?.length
    ? list.categories.flatMap(c => (c.items || []).map(i => `${i.name}${i.qty ? ` — ${i.qty}` : ''}`)).join('\n')
    : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="shopping-list-modal"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl overflow-hidden"
        style={{ background: '#1A1612', border: '1px solid rgba(59,130,246,0.25)' }}>
        <div className="px-5 py-4 flex items-center justify-between flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.15), rgba(59,130,246,0.05))', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-2">
            <ListChecks size={18} weight="fill" className="text-blue-400" />
            <h3 className="text-base font-bold text-white">Smart Shopping List</h3>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white" data-testid="shopping-close-btn">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          {!list && (
            <>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-2 block">Quick pick</label>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { k: 'today', label: 'Today' },
                    { k: 'thisWeek', label: 'This week' },
                    { k: 'nextWeek', label: 'Next week' },
                    { k: 3, label: '3 days' },
                    { k: 7, label: '7 days' },
                    { k: 14, label: '14 days' },
                  ].map(p => (
                    <button key={p.label} onClick={() => setPreset(p.k)}
                      data-testid={`shopping-preset-${typeof p.k === 'number' ? p.k + 'd' : p.k}`}
                      className="px-2.5 py-1 rounded-full text-[10px] font-bold border bg-white/5 text-neutral-300 border-white/10 hover:bg-blue-500/15 hover:text-blue-300 hover:border-blue-500/40">
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1 block">From</label>
                  <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                    data-testid="shopping-date-from"
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:border-blue-400 outline-none" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1 block">To</label>
                  <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                    data-testid="shopping-date-to"
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:border-blue-400 outline-none" />
                </div>
              </div>
              <p className="text-[11px] text-neutral-500 -mt-2">
                <span className={`font-semibold ${dayCount > 31 || dayCount < 1 ? 'text-amber-400' : 'text-blue-400'}`}>{dayCount}</span> day(s)
                {dayCount > 31 && <span className="text-amber-400 ml-2">· Max 31 days</span>}
              </p>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-2 block">Language (for the cook)</label>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setLang('en')} data-testid="shopping-lang-en"
                    className={`py-2 rounded-xl text-sm font-bold border transition-colors ${lang === 'en' ? 'bg-blue-500/15 text-blue-300 border-blue-500/40' : 'bg-white/5 text-neutral-300 border-white/10 hover:bg-white/10'}`}>
                    English
                  </button>
                  <button onClick={() => setLang('hi')} data-testid="shopping-lang-hi"
                    className={`py-2 rounded-xl text-sm font-bold border transition-colors ${lang === 'hi' ? 'bg-blue-500/15 text-blue-300 border-blue-500/40' : 'bg-white/5 text-neutral-300 border-white/10 hover:bg-white/10'}`}>
                    हिन्दी
                  </button>
                </div>
              </div>

              <button onClick={generate} disabled={busy || dayCount < 1 || dayCount > 31}
                data-testid="shopping-generate-btn"
                className="w-full py-2.5 rounded-xl bg-blue-500 text-white text-sm font-bold flex items-center justify-center gap-2 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed">
                {busy ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    {lang === 'hi' ? 'Aggregating + translating…' : 'AI is aggregating ingredients…'}
                  </>
                ) : (
                  <>
                    <Sparkle size={15} weight="fill" />
                    Generate {lang === 'hi' ? 'सूची' : 'shopping list'}
                  </>
                )}
              </button>

              {err && (
                <div className="rounded-lg p-2.5 text-[11px] text-red-300" style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.30)' }}>
                  {err}
                </div>
              )}

              <div className="rounded-lg p-3" style={{ background: 'rgba(59,130,246,0.05)', border: '1px solid rgba(59,130,246,0.15)' }}>
                <p className="text-[11px] font-bold text-blue-300 mb-1.5">How it works</p>
                <ul className="text-[11px] text-neutral-400 space-y-1 list-disc list-inside">
                  <li>AI reads every planned dish in the range and aggregates duplicate ingredients into one quantity.</li>
                  <li>Click any merchant (BigBasket / Zepto / Blinkit / JioMart) to open their search with the item pre-filled.</li>
                  <li>Use <b>Copy items</b> to paste into a merchant&apos;s bulk-order box if they have one.</li>
                </ul>
              </div>
            </>
          )}

          {list && (
            <div className="space-y-4" data-testid="shopping-list-result">
              <div className="rounded-xl p-3 flex flex-wrap items-center justify-between gap-2"
                style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.25)' }}>
                <div>
                  <p className="text-[11px] text-blue-300 font-bold">{list.dishes_count} unique dishes · {list.days} day(s)</p>
                  {list.notes && <p className="text-[10px] text-neutral-500 mt-0.5 italic">{list.notes}</p>}
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => copyToClipboard(allItemsText, 'all')}
                    data-testid="shopping-copy-all-btn"
                    className="px-2.5 py-1 rounded-lg text-[10px] font-bold border bg-white/5 text-neutral-200 border-white/10 hover:bg-white/10 flex items-center gap-1">
                    {copiedAll ? <><Check size={11} weight="bold" className="text-emerald-400" /> Copied!</> : <>Copy all</>}
                  </button>
                  <button onClick={() => { setList(null); setErr(''); }}
                    data-testid="shopping-new-btn"
                    className="px-2.5 py-1 rounded-lg text-[10px] font-bold border bg-white/5 text-neutral-200 border-white/10 hover:bg-white/10">
                    New range
                  </button>
                </div>
              </div>

              {(list.categories || []).map((cat, ci) => {
                const items = cat.items || [];
                if (items.length === 0) return null;
                const catText = items.map(i => `${i.name}${i.qty ? ` — ${i.qty}` : ''}`).join('\n');
                const catQuery = items.map(i => i.name).filter(Boolean).join(' ');
                const catKey = `cat-${ci}`;
                return (
                  <div key={ci} className="rounded-xl overflow-hidden" data-testid={`shopping-cat-${ci}`}
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="px-3 py-2 flex items-center justify-between gap-2 flex-wrap"
                      style={{ background: 'rgba(59,130,246,0.08)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <p className="text-[12px] font-bold text-white uppercase tracking-wider">{cat.name || 'Other'}</p>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[10px] text-neutral-500 mr-1 hidden sm:inline">Shop:</span>
                        {MERCHANTS.map(m => (
                          <a key={m.id} href={m.url(catQuery)} target="_blank" rel="noopener noreferrer"
                            data-testid={`shopping-cat-${ci}-${m.id}`}
                            title={`Search this category on ${m.label}`}
                            style={{ background: m.color, color: m.textColor }}
                            className="px-2 py-0.5 rounded-md text-[10px] font-bold flex items-center gap-1 hover:opacity-90 transition-opacity">
                            {m.label}
                          </a>
                        ))}
                        <button onClick={() => copyToClipboard(catText, catKey)}
                          data-testid={`shopping-cat-${ci}-copy`}
                          className="px-2 py-0.5 rounded-md text-[10px] font-bold border bg-white/5 text-neutral-200 border-white/10 hover:bg-white/10 flex items-center gap-1">
                          {copiedCat === catKey ? <><Check size={10} weight="bold" className="text-emerald-400" /> Copied</> : 'Copy'}
                        </button>
                      </div>
                    </div>
                    <div className="divide-y divide-white/5">
                      {items.map((it, ii) => (
                        <div key={ii} className="px-3 py-2 flex items-center justify-between gap-2 hover:bg-white/5 group">
                          <div className="min-w-0 flex-1">
                            <p className="text-[12px] text-white font-medium truncate">{it.name}</p>
                            {it.qty && <p className="text-[10px] text-neutral-500">{it.qty}</p>}
                          </div>
                          <div className="flex items-center gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
                            {MERCHANTS.map(m => (
                              <a key={m.id} href={m.url(it.name)} target="_blank" rel="noopener noreferrer"
                                data-testid={`shopping-item-${ci}-${ii}-${m.id}`}
                                title={`Search "${it.name}" on ${m.label}`}
                                style={{ background: m.color, color: m.textColor }}
                                className="w-6 h-6 rounded-md text-[9px] font-extrabold flex items-center justify-center hover:scale-110 transition-transform">
                                {m.short}
                              </a>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
